/* ============================================================
   Anniversary Hub — Global Section Slider Controller
   anniversary-hub.js
   ============================================================

   CRITICAL ORDER IN buildHub():
     1. Create hub div (empty)
     2. INSERT hub into DOM ← must happen BEFORE measuring width
     3. panelW = hubEl.offsetWidth  ← real rendered width
     4. Set panel/rail dimensions using panelW in px
   This guarantees pixel-perfect slide alignment regardless of
   container max-width, padding, or viewport size.
   ============================================================ */

(function () {
  'use strict';

  var SECTION_SELECTORS = [
    '.anniversary-banner',
    '.anniversary-about-us',
    '.anniversary-timeline',
    '.anniversary-giveaway',
    '.aniversary-participate-form'
  ];

  var SWIPE_MIN   = 40;
  var SWIPE_RATIO = 1.5;

  /* ── State ── */
  var hubEl        = null;
  var railEl       = null;
  var panelEls     = [];
  var dotEls       = [];
  var currentPanel = 0;
  var isAnimating  = false;
  var panelW       = 0;  /* set AFTER hub is in DOM */
  var panelH       = 0;

  /* ── Drag ── */
  var pStartX   = 0;
  var pStartY   = 0;
  var isDragging = false;

  /* ====================================================
     INNER SLIDER HELPERS
  ==================================================== */

  var aboutUs = {
    get: function (el) {
      var slides = el.querySelectorAll('.ann-hero__slide');
      if (!slides.length) return null;
      var active = el.querySelector('.ann-hero__slide.is-active');
      var idx    = active ? Array.prototype.indexOf.call(slides, active) : 0;
      return { index: idx, last: slides.length - 1 };
    },
    next: function (el) {
      var btn = el.querySelector('.ann-hero__arrow--next');
      if (btn && !btn.disabled) { btn.click(); return true; }
      return false;
    },
    prev: function (el) {
      var btn = el.querySelector('.ann-hero__arrow--prev');
      if (btn && !btn.disabled) { btn.click(); return true; }
      return false;
    }
  };

  var timeline = {
    get: function (el) {
      var sp = getSp(el, '.anntl__splide');
      if (!sp) return null;
      return { index: sp.index, last: sp.length - 1 };
    },
    next: function (el) {
      var sp = getSp(el, '.anntl__splide');
      if (sp && sp.index < sp.length - 1) { sp.go('+1'); return true; }
      return false;
    },
    prev: function (el) {
      var sp = getSp(el, '.anntl__splide');
      if (sp && sp.index > 0) { sp.go('-1'); return true; }
      return false;
    }
  };

  var giveaway = {
    get: function (el) {
      var sp = getSp(el, '#ag-main-splide');
      if (!sp) return null;
      var oIdx = sp.index, oLast = sp.length - 1;
      var ssp  = getSp(el, '#ag-steps-splide');
      if (ssp && oIdx === 1) {
        var dead = ssp.state && typeof ssp.state.is === 'function' && ssp.state.is(5);
        if (!dead && ssp.index < ssp.length - 1) {
          return { index: oIdx, last: oLast + 1 };
        }
      }
      return { index: oIdx, last: oLast };
    },
    next: function (el) {
      var btn = el.querySelector('#ag-main-splide .splide__arrow--next');
      if (btn && !btn.disabled) { btn.click(); return true; }
      return false;
    },
    prev: function (el) {
      var btn = el.querySelector('#ag-main-splide .splide__arrow--prev');
      if (btn && !btn.disabled) { btn.click(); return true; }
      return false;
    }
  };

  var HELPERS = { 1: aboutUs, 2: timeline, 3: giveaway };

  function getSp(panelEl, sel) {
    var el = panelEl.querySelector(sel);
    return (el && el._sp) ? el._sp : null;
  }

  /* ====================================================
     NAVIGATION
  ==================================================== */

  function goForward() {
    if (isAnimating) return;
    var h  = HELPERS[currentPanel];
    var el = panelEls[currentPanel];
    if (h) {
      var info = h.get(el);
      if (info && info.index < info.last) {
        if (h.next(el)) return;
      }
    }
    if (currentPanel < panelEls.length - 1) {
      currentPanel++;
      moveTo(currentPanel);
    }
  }

  function goBack() {
    if (isAnimating) return;
    var h  = HELPERS[currentPanel];
    var el = panelEls[currentPanel];
    if (h) {
      var info = h.get(el);
      if (info && info.index > 0) {
        if (h.prev(el)) return;
      }
    }
    if (currentPanel > 0) {
      currentPanel--;
      moveTo(currentPanel);
    }
  }

  function moveTo(idx) {
    if (!hubEl) return;
    isAnimating = true;
    /* panelW is measured from hubEl.offsetWidth after DOM insertion — always pixel-perfect */
    railEl.style.transform = 'translateX(' + (-idx * panelW) + 'px)';
    updateDots(idx);

    /* Safety reset: ensure no internal scrolling on the panels themselves */
    panelEls.forEach(function(p) { p.scrollLeft = 0; });

    railEl.addEventListener('transitionend', function done() {
      railEl.removeEventListener('transitionend', done);
      isAnimating = false;
    });
  }

  function updateDots(idx) {
    dotEls.forEach(function (d, i) {
      d.classList.toggle('is-active', i === idx);
    });
  }

  /* ====================================================
     POINTER / TOUCH
  ==================================================== */

  function onTouchStart(e) {
    pStartX = e.touches[0].clientX;
    pStartY = e.touches[0].clientY;
  }
  function onTouchEnd(e) {
    var dx = e.changedTouches[0].clientX - pStartX;
    var dy = e.changedTouches[0].clientY - pStartY;
    handleDelta(dx, dy);
  }

  function onMouseDown(e) {
    if (e.button !== 0) return;
    /* Skip drag when the user clicks on an interactive element
       so that inputs/buttons/links work normally */
    var interactive = e.target.closest(
      'input, button, select, textarea, a, label, [role="button"], [tabindex]'
    );
    if (interactive) return;
    isDragging = true;
    pStartX    = e.clientX;
    pStartY    = e.clientY;
    hubEl.classList.add('is-dragging');
    /* No preventDefault — that would block focus on form elements */
  }
  function onMouseMove(e) {
    if (isDragging) e.preventDefault();
  }
  function onMouseUp(e) {
    if (!isDragging) return;
    isDragging = false;
    hubEl.classList.remove('is-dragging');
    handleDelta(e.clientX - pStartX, e.clientY - pStartY);
  }
  function onMouseLeave() {
    if (isDragging) {
      isDragging = false;
      hubEl.classList.remove('is-dragging');
    }
  }

  function handleDelta(dx, dy) {
    if (Math.abs(dx) < SWIPE_MIN) return;
    if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
    if (dx < 0) goForward(); else goBack();
  }

  /* ====================================================
     RESIZE
  ==================================================== */

  function refreshDimensions() {
    if (!hubEl) return;
    /* Re-measure from the live element */
    panelW = hubEl.offsetWidth;
    panelH = getAvailableHeight();

    railEl.style.width = (panelEls.length * panelW) + 'px';
    panelEls.forEach(function (p) {
      p.style.width  = panelW + 'px';
    });

    /* Snap to current panel without animation */
    var tr = railEl.style.transition;
    railEl.style.transition = 'none';
    railEl.style.transform  = 'translateX(' + (-currentPanel * panelW) + 'px)';
    /* Restore transition on next frame */
    requestAnimationFrame(function () { railEl.style.transition = tr; });
  }

  function getAvailableHeight() {
    /* Height = viewport height minus the rendered height of any
       position:sticky or position:fixed header above this section. */
    var headerEl = document.querySelector(
      '.shopify-section-header, [id="shopify-section-header"], header[role="banner"]'
    );
    var headerH = headerEl ? headerEl.offsetHeight : 0;
    return window.innerHeight - headerH;
  }

  /* ====================================================
     DOM CONSTRUCTION
  ==================================================== */

  function findShopifyWrapper(innerEl) {
    var node = innerEl;
    while (node && node !== document.body) {
      if (node.id && node.id.indexOf('shopify-section-') === 0) return node;
      node = node.parentElement;
    }
    return innerEl.parentElement || innerEl;
  }

  function buildHub() {
    /* ── 1. Collect section wrappers ── */
    var wrappers = [];
    var CUSTOM_CLASSES = {
      '.anniversary-banner': 'ann-hub-slide--banner',
      '.anniversary-about-us': 'ann-hub-slide--about',
      '.anniversary-timeline': 'ann-hub-slide--timeline',
      '.anniversary-giveaway': 'ann-hub-slide--giveaway',
      '.aniversary-participate-form': 'ann-hub-slide--form'
    };

    SECTION_SELECTORS.forEach(function (sel) {
      var inner = document.querySelector(sel);
      if (!inner) return;
      var w = findShopifyWrapper(inner);
      if (wrappers.indexOf(w) === -1) {
        /* Add a custom unique class to the Shopify wrapper */
        var customClass = CUSTOM_CLASSES[sel] || sel.replace('.', '');
        w.classList.add(customClass);
        wrappers.push(w);
      }
    });
    if (wrappers.length < 2) return;

    /* ── 2. Find sticky bar wrapper (to move BELOW hub) ── */
    var stickyInner   = document.querySelector('.anniversary-bar');
    var stickyWrapper = stickyInner ? findShopifyWrapper(stickyInner) : null;

    /* ── 3. Create hub skeleton (empty rail) ── */
    hubEl  = document.createElement('div');
    hubEl.className = 'ann-hub';

    railEl = document.createElement('div');
    railEl.className = 'ann-hub__rail';
    hubEl.appendChild(railEl);

    /* ── 4. Record insertion anchor BEFORE removing anything ── */
    var firstWrapper  = wrappers[0];
    var parentNode    = firstWrapper.parentNode;
    /* nextSibling is recorded now while firstWrapper is still in the DOM */
    var stableAnchor  = firstWrapper.nextSibling;

    /* ── 5. Remove all section wrappers from DOM ── */
    wrappers.forEach(function (w) {
      if (w.parentNode) w.parentNode.removeChild(w);
    });

    /* ── 6. INSERT HUB INTO DOM (empty) ← MUST happen before measuring width ── */
    if (stableAnchor && stableAnchor.parentNode === parentNode) {
      parentNode.insertBefore(hubEl, stableAnchor);
    } else {
      parentNode.appendChild(hubEl);
    }

    /* ── 7. NOW measure real pixel width ── */
    panelW = hubEl.offsetWidth;
    panelH = getAvailableHeight();

    /* ── 8. Set hub dimensions ── */
    railEl.style.width   = (wrappers.length * panelW) + 'px';

    /* ── 9. Create panels and move sections into them ── */
    wrappers.forEach(function (wrapper, i) {
      var panel = document.createElement('div');
      panel.className           = 'ann-hub__panel';
      panel.style.width         = panelW + 'px';
      panel.dataset.annPanelIndex = String(i);

      panel.appendChild(wrapper);
      railEl.appendChild(panel);
      panelEls.push(panel);
    });

    /* ── 10. Move sticky bar to AFTER the hub ── */
    if (stickyWrapper) {
      if (stickyWrapper.parentNode) stickyWrapper.parentNode.removeChild(stickyWrapper);
      if (hubEl.nextSibling) {
        hubEl.parentNode.insertBefore(stickyWrapper, hubEl.nextSibling);
      } else {
        hubEl.parentNode.appendChild(stickyWrapper);
      }
    }

    /* ── 11. Progress dots ── */
    var dotsBar = document.createElement('div');
    dotsBar.className = 'ann-hub__dots';
    wrappers.forEach(function (_, i) {
      var dot = document.createElement('div');
      dot.className = 'ann-hub__dot' + (i === 0 ? ' is-active' : '');
      dotsBar.appendChild(dot);
      dotEls.push(dot);
    });
    hubEl.appendChild(dotsBar);  /* inside hub so it scrolls with it */

    /* ── 12. Bind events ── */
    hubEl.addEventListener('touchstart', onTouchStart, { passive: true });
    hubEl.addEventListener('touchend',   onTouchEnd,   { passive: true });
    hubEl.addEventListener('mousedown',  onMouseDown);
    hubEl.addEventListener('mousemove',  onMouseMove);
    hubEl.addEventListener('mouseup',    onMouseUp);
    hubEl.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('resize',   debounce(refreshDimensions, 150));

    /* ── 13. Build section-ID → panel-index map, then wire banner tabs ── */
    wireBannerTabs();

    /* ── 14. Patch Splide instances ── */
    setTimeout(patchSplide, 800);
  }

  /* ====================================================
     BANNER TAB → HUB NAVIGATION
     Intercept clicks on .anniversary-banner__tab and
     .anniversary-banner__fab-tab so that data-target-section
     navigates the hub instead of trying to window.scrollTo.
  ==================================================== */

  function wireBannerTabs() {
    /* Build map: sectionId → panelIndex by scanning each panel's DOM */
    var idMap = {};
    panelEls.forEach(function (panel, i) {
      /* Check direct Shopify wrapper IDs */
      panel.querySelectorAll('[id]').forEach(function (el) {
        if (el.id) idMap[el.id] = i;
      });
      /* Also map by CSS class names used in target_section_id fields */
      var classMap = [
        ['anniversary-about-us',        1],
        ['anniversary-timeline',         2],
        ['anniversary-giveaway',         3],
        ['aniversary-participate-form',  4],
        ['anniversary-participate',      4]
      ];
      classMap.forEach(function (pair) {
        if (panel.querySelector('.' + pair[0])) idMap[pair[0]] = pair[1];
      });
    });

    /* Listen on both desktop tabs, mobile FAB tabs, and Thank You buttons */
    hubEl.addEventListener('click', function (e) {
      var tab = e.target.closest(
        '.anniversary-banner__tab, .anniversary-banner__fab-tab, .ty-action-btn--primary'
      );
      if (!tab) return;
      
      var targetId = tab.dataset.targetSection || tab.getAttribute('href');
      if (!targetId) return;
      
      /* Clean up '#' if present */
      targetId = targetId.replace('#', '');
      
      var idx = idMap[targetId];
      if (idx === undefined) return;
      
      e.preventDefault();
      e.stopPropagation();
      currentPanel = idx;
      moveTo(idx);
    });
  }

  /* ====================================================
     SPLIDE PATCH
  ==================================================== */

  function patchSplide() {
    if (typeof Splide === 'undefined') return;
    if (Array.isArray(Splide.instances)) {
      Splide.instances.forEach(function (sp) {
        if (sp && sp.root && !sp.root._sp) sp.root._sp = sp;
      });
    }
  }

  /* ====================================================
     UTILITIES
  ==================================================== */

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ====================================================
     BOOT
  ==================================================== */

  function boot() {
    var count = 0;
    SECTION_SELECTORS.forEach(function (sel) {
      if (document.querySelector(sel)) count++;
    });
    if (count < 2) return;
    buildHub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
